import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import { SimpleIntent } from "@/model";
import { useIntentNames } from "@/query/intent";
import { useCreateSelector, useUpdateSelector } from "@/query/selector";
import { editingSelectorAtom } from "@/atom/editing";
import { useAgentId } from "@/hook";
import Button from "@/component/Button";
import { errorToast, successToast } from "@/component/Toast";
import { TextInputGroup, Typeahead, TypeaheadOptions, TypeaheadInput } from "@/component/input";


const SelectorForm: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();

    const [sentence, setSentence] = useState<string>("");
    const [intent, setIntent] = useState<SimpleIntent | undefined>(undefined);
    const [intentSearchKeyword, setIntentSearchKeyword] = useState<string>("");
    const [validation, setValidation] = useState<{
        sentence: boolean,
        intent: boolean
    }>({
        sentence: true,
        intent: true
    });

    const [editingSelector, setEditingSelector] = useAtom(editingSelectorAtom);
    const { data: intents } = useIntentNames(agentId);
    const { mutateAsync: createSelector, isPending: isCreating } = useCreateSelector(agentId);
    const { mutateAsync: updateSelector, isPending: isUpdating } = useUpdateSelector(agentId, editingSelector?.id);

    const activeIntents = useMemo(() => {
        return intents?.filter(intent => intent.active) ?? [];
    }, [intents]);
    const filteredIntents = useMemo(() => {
        if (!intentSearchKeyword) {
            return activeIntents;
        }
        return activeIntents.filter(intent => intent.name.toLowerCase().includes(intentSearchKeyword.toLowerCase()));
    }, [activeIntents, intentSearchKeyword]);

    const clear = useCallback(() => {
        setSentence("");
        setIntentSearchKeyword("");
        setIntent(undefined);
        setEditingSelector(undefined);
        setValidation({ sentence: true, intent: true });
    }, [setValidation, setEditingSelector]);

    const validate = useCallback(() => {
        const currentValidation = {
            sentence: !!sentence.trim(),
            intent: !!intent
        };
        setValidation(currentValidation);
        return Object.values(currentValidation).every(value => value);
    }, [sentence, intent]);

    const onSubmit = async () => {
        if (!validate()) {
            return;
        }

        const form = {
            sentence: sentence.trim(),
            intent_id: intent!.id,
            intent_name: intent!.name
        };
        if (editingSelector) {
            await updateSelector(form)
                .then(() => {
                    successToast(intl.formatMessage({ id: "t003" }));
                    clear();
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: "t004" }));
                });
        } else {
            await createSelector(form)
                .then(() => {
                    successToast(intl.formatMessage({ id: "t001" }));
                    clear();
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: "t002" }));
                });
        }
    };

    const onChangeIntent = (intent_name?: string | string[]) => {
        if (intent_name) {
            setIntent(activeIntents!.find(intent => intent.name === intent_name));
        } else {
            setIntent(undefined);
        }
    };

    useEffect(() => {
        return () => {
            clear();
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (editingSelector) {
            setSentence(editingSelector.sentence);
            setIntent(intents!.find(intent => intent.id === editingSelector.intent_id));
        } else {
            setSentence("");
            setIntent(undefined);
        }
    }, [editingSelector, intents]);

    return (
        <div role="form" className="form-container">
            <div className="form-container-input-group">
                <TextInputGroup rowGap={8}
                                value={sentence}
                                label={intl.formatMessage({ id: "i04005" })}
                                placeholder={intl.formatMessage({ id: "i04006" })}
                                invalid={!validation.sentence && !sentence.trim()}
                                invalidText={intl.formatMessage({ id: "i04007" })}
                                onChange={setSentence}
                />

                <div className="flex flex-col gap-2">
                    <div className="input-group-heading">
                        {intl.formatMessage({ id: "i04008" })}
                    </div>

                    <Typeahead selection={intent?.name ?? ""}
                               onChange={onChangeIntent}
                    >
                        <TypeaheadInput selection={intent?.name ?? ""}
                                        placeholder={intl.formatMessage({ id: "i04009" })}
                                        invalid={!validation.intent && !intent}
                                        invalidText={intl.formatMessage({ id: "i04010" })}
                                        onBlur={() => setIntentSearchKeyword("")}
                                        onChange={e => setIntentSearchKeyword(e.target.value)}
                        />

                        <TypeaheadOptions options={filteredIntents.map(intent => intent.name)} />
                    </Typeahead>
                </div>
            </div>

            <div className="flex gap-2">
                {editingSelector && (
                    <Button variant="secondary"
                            size="full"
                            disabled={isUpdating || isCreating}
                            onClick={clear}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                )}
                <Button variant="primary"
                        size="full"
                        disabled={isUpdating || isCreating}
                        onClick={onSubmit}
                >
                    {intl.formatMessage({ id: editingSelector ? "b0001" : "b0010" })}
                </Button>
            </div>
        </div>
    );
};

export default SelectorForm;
